import type { Difficulty } from '../domain/puzzle/catalog';
import { themes } from '../domain/puzzle/catalog';

// Original compact line drawings; labels carry the meaning alongside every icon.
const paths: Record<(typeof themes)[number], string> = {
  fork: 'M12 21V12L5 5M12 12l7-7M2 5h6M5 2v6M16 5h6M19 2v6',
  pin: 'M12 22V12M7 3h10l-2 5 3 4H6l3-4Z',
  skewer: 'M3 21 21 3M4 8l4-4 4 4-4 4ZM12 16l4-4 4 4-4 4Z',
  discoveredAttack: 'M3 18h18l-4-4M21 18l-4 4M8 14V3l-4 4M8 3l4 4',
  doubleCheck: 'm2 8 3 3 5-6m4 3 3 3 5-6M4 18h16',
  sacrifice: 'M12 21 3 12V6l4-3 5 4 5-4 4 3v6ZM9 12h6m-3-3v6',
  deflection: 'M3 20 12 11h8m-4-4 4 4-4 4M3 4l5 5',
  attraction: 'M3 4l6 6M3 10h6V4M21 4l-6 6m0-6v6h6M9 19a3 3 0 1 0 6 0 3 3 0 1 0-6 0',
  hangingPiece: 'M12 2v4m-4 4a4 4 0 1 0 8 0 4 4 0 1 0-8 0M10 14l-3 7h10l-3-7',
  trappedPiece: 'M3 9V3h6m6 0h6v6m0 6v6h-6m-6 0H3v-6M8 12l4-4 4 4-4 4Z',
  defensiveMove: 'M12 2 3 6v6c0 5 9 10 9 10s9-5 9-10V6ZM8 12l3 3 5-6',
  quietMove: 'M4 18h16M7 14V9a5 5 0 0 1 10 0v5M10 21h4M3 3l18 18',
  intermezzo: 'M3 6h18M3 18h18M13 3l-5 10h7l-4 8',
  backRankMate: 'M3 3v5h18V3M6 3v5m6-5v5m6-5v5M8 21l1-8h6l1 8ZM7 21h10',
  mateIn1: 'M4 21h16M7 17l-2-9 7 4 7-4-2 9ZM12 2v6m-3-3h6M11 14h1v3',
  mateIn2: 'M4 21h16M7 17l-2-9 7 4 7-4-2 9ZM12 2v6m-3-3h6M10 14h4l-4 3h4',
  mateIn3: 'M4 21h16M7 17l-2-9 7 4 7-4-2 9ZM12 2v6m-3-3h6M10 14h4l-2 1 2 2h-4',
  promotion: 'M12 3v12M7 8l5-5 5 5M5 21h14M8 18h8',
  underPromotion: 'M5 21h14l-2-6V7l-5-4-6 7 5 1-4 7M13 7h1',
  endgame: 'M5 3h14M5 21h14M7 3v4l10 10v4M17 3v4L7 17v4',
  advantage: 'M3 19h18M6 15v-3m6 3V8m6 7V3',
  crushing: 'M3 21h18M5 17l5-7 4 3 6-10M14 3h6v6',
};
const difficultyPaths: Record<Difficulty | 'adaptive', string> = {
  easy: 'M5 21V14h4v7M12 21h7',
  normal: 'M3 21V15h4v6m3 0V9h4v12m3 0h4',
  hard: 'M3 21V15h4v6m3 0V9h4v12m3 0V3h4v18',
  expert: 'M4 20h16M6 16 3 6l6 4 3-7 3 7 6-4-3 10Z',
  mixed: 'M3 6h4l10 12h4m-4-4 4 4-4 4M3 18h4l10-12h4m-4-4 4 4-4 4',
  custom: 'M3 6h18M3 12h18M3 18h18M8 3v6m8 0v6m-6 0v6',
  // R5: a dial whose needle settles where you are.
  adaptive: 'M3 17a9 9 0 0 1 18 0M12 17l4-5M6 12l1 1M12 8v1M18 12l-1 1',
};
export function PracticeIcon({ name, difficulty = false }: { name: string; difficulty?: boolean }) {
  const path = difficulty ? difficultyPaths[name as Difficulty | 'adaptive'] : paths[name as keyof typeof paths];
  return (
    <svg
      className="practice-icon"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d={path} />
    </svg>
  );
}
